// src/components/mdx/client/visualizations/CreativeTestConfidenceChart.client.tsx

/**
 * Grouped bar chart with confidence intervals for creative A/B test readouts.
 */

'use client'

import { useMemo } from 'react'

import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BlogChartPlotArea } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { generateCreativeTestData } from '~/components/mdx/client/visualizations/marketingDataVizData'

interface CreativeTestConfidenceChartContentProps {
  isActive: boolean
}

function CreativeTestConfidenceChartContent(_props: CreativeTestConfidenceChartContentProps): JSX.Element {
  const data = useMemo(() => generateCreativeTestData(), [])
  const minCi = Math.min(...data.map(variant => variant.ciLow))
  const maxCi = Math.max(...data.map(variant => variant.ciHigh))
  const range = maxCi - minCi

  return (
    <BlogChartShell
      title="Creative Test Readout"
      description="Compare variant CPA with 95% confidence intervals — overlapping intervals mean no clear winner yet."
      gradient="from-emerald-400 to-indigo-400"
    >
      <BlogChartPlotArea className="h-auto">
        <div className="space-y-4 p-4 sm:p-6" aria-label="Creative variant CPA confidence interval readout">
          <div className="hidden grid-cols-[minmax(7rem,10rem)_1fr_minmax(3rem,4rem)] items-end gap-4 border-b border-white/10 pb-2 text-[11px] font-medium uppercase tracking-[0.16em] text-gray-500 sm:grid">
            <span>Variant</span>
            <span>95% confidence interval</span>
            <span className="text-right">CPA</span>
          </div>
          {data.map(variant => {
            const ciLeft = ((variant.ciLow - minCi) / range) * 100
            const ciWidth = ((variant.ciHigh - variant.ciLow) / range) * 100
            const cpaLeft = ((variant.cpa - minCi) / range) * 100

            return (
              <div key={variant.variant} className="space-y-2 rounded-xl border border-white/10 bg-slate-950/35 p-3 sm:grid sm:grid-cols-[minmax(7rem,10rem)_1fr_minmax(3rem,4rem)] sm:items-center sm:gap-4 sm:space-y-0 sm:border-0 sm:bg-transparent sm:p-0">
                <div className="flex min-w-0 items-start justify-between gap-3 sm:block">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold leading-tight text-gray-100">{variant.variant}</p>
                    <p className="text-[11px] text-gray-500">{variant.impressions.toLocaleString()} impressions</p>
                  </div>
                  <p className="shrink-0 text-sm font-semibold tabular-nums text-white sm:hidden">${variant.cpa.toFixed(1)}</p>
                </div>
                <div className="relative h-9 rounded-full bg-white/[0.04]">
                  <div
                    className="absolute top-1/2 h-2 -translate-y-1/2 rounded-full bg-slate-400/55"
                    style={{ left: `${ciLeft}%`, width: `${ciWidth}%` }}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute top-1/2 h-6 w-1.5 -translate-y-1/2 rounded-full bg-cyan-300 shadow-[0_0_16px_rgba(34,211,238,0.55)]"
                    style={{ left: `${cpaLeft}%` }}
                    aria-hidden="true"
                  />
                  <div
                    className="absolute inset-y-1 rounded-full bg-gradient-to-r from-violet-500/30 to-cyan-400/20 opacity-80"
                    style={{ left: `${Math.max(0, cpaLeft - 2)}%`, width: '4%' }}
                    aria-hidden="true"
                  />
                </div>
                <p className="hidden text-right text-sm font-semibold tabular-nums text-white sm:block">${variant.cpa.toFixed(1)}</p>
                <p className="sr-only">
                  {variant.variant}: CPA ${variant.cpa.toFixed(1)}, 95% confidence interval ${variant.ciLow.toFixed(1)} to ${variant.ciHigh.toFixed(1)}.
                </p>
              </div>
            )
          })}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-t border-white/10 pt-3 text-[11px] text-gray-400 sm:text-xs">
            <span>${minCi.toFixed(0)} lower CI</span>
            <span className="font-medium text-cyan-200/80">Intervals overlap: keep testing</span>
            <span>${maxCi.toFixed(0)} upper CI</span>
          </div>
        </div>
      </BlogChartPlotArea>
    </BlogChartShell>
  )
}

export default function CreativeTestConfidenceChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Creative test confidence interval chart">
      {({ isIntersecting }) => (
        <CreativeTestConfidenceChartContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
